import { useMemo } from 'react';
import { WardSearch } from './WardSearch';
import { WardFilters } from './WardFilters';
import { WardList } from './WardList';
import { useWardStore } from '@/stores/wardStore';
import { useUIStore } from '@/stores/uiStore';
import { WardListSkeleton } from '@/components/common/SkeletonLoader';
import { VI_MESSAGES } from '@/constants/vietnamese';
import { normalizeVietnameseString } from '@/utils/vietnamese-format';

export function WardListTab() {
  const { wards, filters } = useWardStore();
  const isDataLoading = useUIStore((state) => state.isDataLoading);

  // Lọc và sắp xếp danh sách phường
  const filteredWards = useMemo(() => {
    let filtered = [...wards];

    // Tìm kiếm theo tên
    if (filters.searchTerm) {
      const normalizedSearch = normalizeVietnameseString(filters.searchTerm.toLowerCase());
      filtered = filtered.filter((ward) => {
        const normalizedName = normalizeVietnameseString(ward.xaphuong.toLowerCase());
        return normalizedName.includes(normalizedSearch);
      });
    }

    // Lọc theo quận
    if (filters.selectedDistrict) {
      filtered = filtered.filter((ward) => ward.xaphuong === filters.selectedDistrict);
    }

    // Lọc theo diện tích
    if (filters.areaRange) {
      const [minArea, maxArea] = filters.areaRange;
      filtered = filtered.filter(
        (ward) => ward.dientichpl >= minArea && ward.dientichpl <= maxArea
      );
    }

    // Lọc theo dân số
    if (filters.populationRange) {
      const [minPop, maxPop] = filters.populationRange;
      filtered = filtered.filter(
        (ward) => ward.danso && ward.danso >= minPop && ward.danso <= maxPop
      );
    }

    // Sắp xếp
    filtered.sort((a, b) => {
      let compareValue = 0;
      
      switch (filters.sortBy) {
        case 'name':
          compareValue = a.xaphuong.localeCompare(b.xaphuong, 'vi');
          break;
        case 'area':
          compareValue = (a.dientichpl || 0) - (b.dientichpl || 0);
          break;
        case 'population':
          compareValue = (a.danso || 0) - (b.danso || 0);
          break;
      }

      return filters.sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [wards, filters]);

  return (
    <div className="flex h-full flex-col">
      {/* Tìm kiếm và bộ lọc */}
      <div className="space-y-3 p-4">
        <WardSearch />
        <WardFilters />
      </div>

      {/* Danh sách phường */}
      <div className="flex-1 overflow-hidden">
        {isDataLoading ? (
          <WardListSkeleton />
        ) : filteredWards.length > 0 ? (
          <WardList wards={filteredWards} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <p className="text-gray-500">{VI_MESSAGES.WARD.NO_RESULTS}</p>
            <button
              onClick={() => useWardStore.getState().resetFilters()}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              {VI_MESSAGES.WARD.ACTIONS.CLEAR_FILTERS}
            </button>
          </div>
        )}
      </div>

      {/* Thống kê */}
      <div className="border-t bg-gray-50 px-4 py-2 text-sm text-gray-600">
        {VI_MESSAGES.WARD.LABELS.TOTAL}: {filteredWards.length} / {wards.length} phường
      </div>
    </div>
  );
}
